import { useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import Button from "../../components/Button";
import Input from "../../components/Input";
import TermsAndConditions from "./components/TermsAndConditions";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import createRegisterSchema from "./schemas/registerSchema";

import { registerUser, sendAdminWelcomeEmail } from "./api/onboardingRequest";
import { Select } from "@headlessui/react";
import { useTranslation } from "../../i18n/TranslationProvider";

const SignUp = () => {
    const { login, userProfile } = useAuth();
    const navigate = useNavigate();
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
    const [error, setError] = useState("");
    const [productType, setProductType] = useState("Sports");
    const { t } = useTranslation();
    
    const ADMIN_USER_TYPES = ["Administrator", "Athletic Director/Administrator"];

    const sportsUserTypes = [
        { value: "Athletic Director/Administrator", label: t("onboarding.userTypeAthleticDirector") },
        { value: "Athletic Trainer", label: t("onboarding.userTypeAthleticTrainer") },
        { value: "Chiropractor", label: t("onboarding.userTypeChiropractor") },
        { value: "Coach", label: t("onboarding.userTypeCoach") },
        { value: "Orthopedist", label: t("onboarding.userTypeOrthopedist") },
        { value: "Sports Medicine", label: t("onboarding.userTypeSportsMedicine") },
        { value: "Other", label: t("onboarding.other") },
    ];

    const emrUserTypes = [
        { value: "Administrator", label: t("onboarding.userTypeAdministrator") },
        { value: "Emergency Med Tech", label: t("onboarding.userTypeEmergencyMedTech") },
        { value: "Firefighter", label: t("onboarding.userTypeFirefighter") },
        { value: "Police Officer", label: t("onboarding.userTypePoliceOfficer") },
        { value: "Other", label: t("onboarding.other") },
    ];

    const registerSchema = useMemo(() => createRegisterSchema(t), [t]);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        watch,
    } = useForm({
        resolver: yupResolver(registerSchema),
    });

    const iissType = watch("iissType");

    const onSubmit = async (data) => {
        setError("");

        if (!termsAgreed) {
            setError(t("onboarding.termsAgreementRequired"));
            return;
        }

        try {
            const res = await registerUser({
                ...data,
                city: "not required",
                state: "not required",
            });

            login(res.data.userId);

            // TODO: TEMPORARY — replace localStorage with real user DB field once backend is ready
            // localStorage.setItem("productType", productType);

            // only send welcom email to admins
            if (ADMIN_USER_TYPES.includes(data.userType)) {
                await sendAdminWelcomeEmail(data);
            }
            navigate("/payment");
        } catch (err) {
            if (err.response?.status === 409) {
                setError(t("onboarding.emailAlreadyExists"));
            } else {
                setError(
                    err?.response?.data?.message ||
                        t("onboarding.registrationFailedGeneric")
                );
            }
        }
    };

    return (
        <>
            <div
                className="custom-container mx-auto w-4/5 lg:w-1/2"
                style={{ display: showTermsAndConditions ? "none" : "block" }}
            >
                <div className="text-center mx-auto mb-6">
                    <h4 className="text-lg md:text-2xl font-semibold mb-2">
                        {t("onboarding.createAccountHeading")}
                    </h4>
                    <p>
                        {t("onboarding.createAccountSubheading")}
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-4"
                >
                    <Input
                        label={t("onboarding.firstNameLabel")}
                        name="firstName"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label={t("onboarding.lastNameLabel")}
                        name="lastName"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label={t("auth.email")}
                        type="email"
                        name="email"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label={t("onboarding.dobLabel")}
                        type="date"
                        name="dob"
                        register={register}
                        errors={errors}
                        max={new Date().toISOString().split('T')[0]}
                        placeholder="MM-DD-YYYY"
                    />
                    {/* <div>
                        <label htmlFor="productType" className="block mb-1 text-sm md:text-base">Product Type</label>
                        <select
                            id="productType"
                            className="w-full border border-primary rounded-md px-3 py-2 bg-secondary"
                            value={productType}
                            onChange={(e) => setProductType(e.target.value)}
                        >
                            <option value="Sports">Sports</option>
                            <option value="EMR">EMR</option>
                        </select>
                    </div> */}
                    <Input 
                        label={t("onboarding.productTypeLabel")}
                        name="iissType"
                        type="select"
                        register={register}
                        errors={errors}
                        options={[
                            {
                                value: "sports",
                                label: t("onboarding.productTypeSports")
                            },
                            {
                                value: "emr",
                                label: t("onboarding.productTypeEmr")
                            }
                        ]}
                    />
                    <Input
                        label={t("onboarding.userTypeLabel")}
                        name="userType"
                        type="select"
                        register={register}
                        errors={errors}
                        options={iissType === "emr" ? emrUserTypes : sportsUserTypes}
                    />
                    <Input
                        label={t("onboarding.genderLabel")}
                        name="gender"
                        type="select"
                        register={register}
                        errors={errors}
                        options={[
                            {
                                value: "male",
                                label: t("onboarding.genderMale"),
                            },
                            {
                                value: "female",
                                label: t("onboarding.genderFemale"),
                            },
                            {
                                value: "other",
                                label: t("onboarding.other"),
                            },
                        ]}
                    />
                    <Input
                        label={t("onboarding.organizationNameLabel")}
                        name="organizationName"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label={t("onboarding.postalCodeLabel")}
                        name="postalCode"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label={t("auth.password")}
                        type="password"
                        name="password"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        label={t("onboarding.confirmPasswordLabel")}
                        type="password"
                        name="confirmPassword"
                        register={register}
                        errors={errors}
                    />

                    {error && <p className="text-red-600">{error}</p>}

                    <p>
                        {t("onboarding.acceptTermsPrefix")}{" "}
                        <span
                            className="text-teal-600 text-semibold underline cursor-pointer"
                            onClick={() => {
                                window.scrollTo({ top: 0 });
                                setShowTermsAndConditions(true);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    window.scrollTo({ top: 0 });
                                    setShowTermsAndConditions(true);
                                }
                            }}
                        >
                            {t("onboarding.termsAndAgreementsLink")}
                        </span>
                        .
                    </p>

                    <Button type="submit" isLoading={isSubmitting}>
                        {t("auth.register")}
                    </Button>
                </form>
                <hr className="mt-6 border border-gray-200 w-full p-0 mx-auto" />
                <div className="mt-4 px-2 flex justify-between items-center">
                    <p>{t("onboarding.alreadyHaveAccount")}</p>
                    <h6
                        className="text-teal-600 cursor-pointer"
                        onClick={() => navigate("/login")}
                    >
                        {t("auth.login")}
                    </h6>
                </div>
                <div className="mt-2 px-2 flex justify-between items-center">
                    <p>{t("auth.needHelp")}</p>
                    <h6
                        className="text-teal-600 cursor-pointer"
                        onClick={() => navigate("/help")}
                    >
                        {t("auth.contactUs")}
                    </h6>
                </div>
            </div>

            {showTermsAndConditions && (
                <div className="custom-container mx-auto w-4/5 lg:w-1/2 ">
                    <TermsAndConditions
                        firstName={watch("firstName")}
                        lastName={watch("lastName")}
                        termsAgreed={termsAgreed}
                        setTermsAgreed={setTermsAgreed}
                        setShowTermsAndConditions={setShowTermsAndConditions}
                        setError={setError}
                    />
                </div>
            )}
        </>
    );
};

export default SignUp;